import { Badge } from '@/shared/ui/Badge';
import type { BadgeVariant } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { clsx } from 'clsx';
import type { Review } from '../api/reviewsApi';
import { formatDate } from '../utils/formatDate';
import { StarRating } from './StarRating';
import { Avatar } from './Avatar';
import styles from './ReviewCard.module.css';

interface Props {
  review: Review;
  onRespond: (review: Review) => void;
}

/**
 * Badge reflects the actual reply state, not the internal status: a review that
 * already has a reply on Google (existingReplyText) reads "Replied", never "No reply".
 */
function getBadge(review: Review): { label: string; variant: BadgeVariant } {
  if (review.status === 'posted' || review.existingReplyText) {
    return { label: 'Replied', variant: 'success' };
  }
  switch (review.status) {
    case 'draft_ready':
      return { label: 'Needs reply', variant: 'warning' };
    case 'scheduled':
      return { label: 'Scheduled', variant: 'info' };
    case 'failed':
      return { label: 'Reply failed', variant: 'error' };
    case 'pending_draft':
      return { label: 'Drafting…', variant: 'neutral' };
    case 'held_manual':
      return { label: 'Needs reply', variant: 'warning' };
    case 'skipped':
    default:
      return { label: 'No reply needed', variant: 'neutral' };
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time}`;
}

export function ReviewCard({ review, onRespond }: Props) {
  const badge = getBadge(review);
  const canRespond =
    review.status === 'draft_ready' ||
    review.status === 'failed' ||
    review.status === 'held_manual' ||
    (review.status === 'skipped' && !review.existingReplyText);
  const reply =
    review.status === 'posted'
      ? { time: review.postedAt, text: review.postedReplyText }
      : review.existingReplyText
        ? { time: review.existingReplyTime, text: review.existingReplyText }
        : null;
  const replyLabel = reply
    ? reply.time
      ? `Replied on ${formatDateTime(reply.time)}`
      : 'Replied'
    : '';

  return (
    <div className={clsx(patterns.panel, styles.card)}>
      <div className={styles.header}>
        <div className={styles.author}>
          <Avatar name={review.reviewerName} photoUrl={review.reviewerPhotoUrl} />
          <div>
            <div className={styles.name}>{review.reviewerName ?? 'Anonymous'}</div>
            <div className={styles.meta}>
              <StarRating rating={review.rating} />
              <span className={styles.date}>{formatDate(review.reviewCreateTime)}</span>
            </div>
          </div>
        </div>
        <div className={styles.badges}>
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {review.editedByHuman && review.hipaaPassed === false && (
            <Badge variant="warning">Compliance</Badge>
          )}
        </div>
      </div>

      {review.comment ? (
        <p className={styles.comment}>{review.comment}</p>
      ) : (
        <p className={styles.noComment}>This patient left a star rating with no written review.</p>
      )}

      {review.status === 'scheduled' && review.scheduledPostAt && (
        <p className={styles.scheduled}>
          Auto-reply scheduled for {formatDate(review.scheduledPostAt)}.
        </p>
      )}

      {review.status === 'failed' && review.failureReason && (
        <p className={styles.failure}>{review.failureReason}</p>
      )}

      {reply && (
        <div className={styles.reply}>
          <span className={styles.replyLabel}>{replyLabel}</span>
          <p className={styles.replyText}>{reply.text}</p>
        </div>
      )}

      {canRespond && (
        <div className={styles.actions}>
          <Button size="sm" onClick={() => onRespond(review)}>
            {review.status === 'failed'
              ? 'Retry reply'
              : review.draftText
                ? 'Review & respond'
                : 'Write a reply'}
          </Button>
        </div>
      )}
    </div>
  );
}
